import type { ResolvedPalette } from '@/api/types/generated'

const ON_DARK = '#1F2937'
const ON_LIGHT = '#FFFFFF'

function parseHex(hex: string): [number, number, number] {
  const v = Number.parseInt(hex.slice(1), 16)
  return [(v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff]
}

function shade(rgb: [number, number, number], factor: number): string {
  const s = (c: number) =>
    Math.round(c * factor)
      .toString(16)
      .padStart(2, '0')
      .toUpperCase()
  return `#${s(rgb[0])}${s(rgb[1])}${s(rgb[2])}`
}

function luminance([r, g, b]: [number, number, number]): number {
  const lin = (c: number) => {
    const v = c / 255
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

export function contrastRatio(hexA: string, hexB: string): number {
  const l1 = luminance(parseHex(hexA))
  const l2 = luminance(parseHex(hexB))
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1]
  return (hi + 0.05) / (lo + 0.05)
}

export function derivePalette(hex: string): ResolvedPalette {
  const rgb = parseHex(hex)
  const onBrand = contrastRatio(ON_LIGHT, hex) >= contrastRatio(ON_DARK, hex) ? ON_LIGHT : ON_DARK
  const best = Math.max(contrastRatio(ON_LIGHT, hex), contrastRatio(ON_DARK, hex))
  return {
    brand: hex.toUpperCase(),
    brand_hover: shade(rgb, 0.92),
    brand_active: shade(rgb, 0.86),
    brand_soft: `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.10)`,
    on_brand: onBrand,
    focus_ring: `rgba(${rgb[0]},${rgb[1]},${rgb[2]},0.35)`,
    contrast_warning: best < 4.5,
  }
}
